/**
 * API Client for Chatbot Monetique
 *
 * Handles all communication with the FastAPI backend
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  Chatbot,
  ChatbotCreate,
  ChatbotUpdate,
  Conversation,
  ConversationCreate,
  ConversationUpdate,
  ConversationChatRequest,
  Document,
  Message,
  ChatRequest,
  ChatResponse,
  UserSession,
  PaginatedResponse,
  PaginationParams,
  HealthResponse,
  AppInfoResponse,
  ApiError,
  SourceDocument,
} from '@/types/api';

class ApiClient {
  private client: AxiosInstance;
  private sessionToken: string | null = null;

  constructor(baseURL: string = '/api') {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add session token
    this.client.interceptors.request.use(
      (config) => {
        if (this.sessionToken) {
          config.headers['X-Session-Token'] = this.sessionToken;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<{ detail?: string }>) => {
        const apiError: ApiError = {
          error: error.message,
          detail: error.response?.data?.detail || error.response?.statusText,
          code: error.code || error.response?.status?.toString(),
        };
        return Promise.reject(apiError);
      }
    );
  }

  setSessionToken(token: string) {
    this.sessionToken = token;
  }

  clearSessionToken() {
    this.sessionToken = null;
  }

  // ==================== Health & Info ====================

  async getHealth(): Promise<HealthResponse> {
    const response = await this.client.get<HealthResponse>('/health');
    return response.data;
  }

  async getAppInfo(): Promise<AppInfoResponse> {
    const response = await this.client.get<AppInfoResponse>('/info');
    return response.data;
  }

  // ==================== Chatbots ====================

  async getChatbots(params?: PaginationParams): Promise<PaginatedResponse<Chatbot>> {
    const response = await this.client.get<PaginatedResponse<Chatbot>>('/chatbots/', { params });
    return response.data;
  }

  async getChatbot(id: string): Promise<Chatbot> {
    const response = await this.client.get<Chatbot>(`/chatbots/${id}`);
    return response.data;
  }

  async createChatbot(chatbot: ChatbotCreate): Promise<Chatbot> {
    const response = await this.client.post<Chatbot>('/chatbots/', chatbot);
    return response.data;
  }

  async updateChatbot(id: string, updates: ChatbotUpdate): Promise<Chatbot> {
    const response = await this.client.put<Chatbot>(`/chatbots/${id}`, updates);
    return response.data;
  }

  async deleteChatbot(id: string): Promise<void> {
    await this.client.delete(`/chatbots/${id}`);
  }

  async clearChatbotData(id: string): Promise<Chatbot> {
    const response = await this.client.post<Chatbot>(`/chatbots/${id}/clear`);
    return response.data;
  }

  async getChatbotStats(id: string): Promise<any> {
    const response = await this.client.get(`/chatbots/${id}/stats`);
    return response.data;
  }

  // ==================== Documents ====================

  async getDocuments(
    chatbotId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Document>> {
    const response = await this.client.get<PaginatedResponse<Document>>(
      `/documents/${chatbotId}`,
      { params }
    );
    return response.data;
  }

  async uploadLocalFiles(chatbotId: string, files: FileList): Promise<Document[]> {
    const formData = new FormData();

    Array.from(files).forEach((file) => {
      formData.append('files', file);
    });

    const response = await this.client.post<Document[]>(
      `/documents/upload-local/${chatbotId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  async deleteDocument(id: string): Promise<void> {
    await this.client.delete(`/documents/${id}`);
  }

  async downloadDocument(id: string): Promise<Blob> {
    const response = await this.client.get(`/documents/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getDocumentStats(chatbotId: string): Promise<any> {
    const response = await this.client.get(`/documents/stats/${chatbotId}`);
    return response.data;
  }

  // ==================== Chat ====================

  async chat(request: ChatRequest): Promise<ChatResponse> {
    const response = await this.client.post<ChatResponse>(
      `/chat/${request.chatbot_id}`,
      request
    );
    return response.data;
  }

  async *chatStream(request: ChatRequest): AsyncGenerator<string | { sources: SourceDocument[] }, void, unknown> {
    const streamBaseUrl = '/api';
    const response = await fetch(`${streamBaseUrl}/chat/${request.chatbot_id}/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') {
              return;
            } else if (data.startsWith('[ERROR]')) {
              throw new Error(data.slice(7));
            } else if (data.startsWith('[SOURCES]')) {
              try {
                const sources: SourceDocument[] = JSON.parse(data.slice(9));
                yield { sources };
              } catch (e) {
                console.warn('Failed to parse sources:', e);
              }
            } else {
              try {
                yield JSON.parse(data);
              } catch {
                yield data;
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  async getChatHistory(
    chatbotId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Message>> {
    const response = await this.client.get<PaginatedResponse<Message>>(
      `/chat/${chatbotId}/messages`,
      { params }
    );
    return response.data;
  }

  async clearChatHistory(chatbotId: string): Promise<void> {
    await this.client.delete(`/chat/${chatbotId}/messages`);
  }

  async createMessage(chatbotId: string, message: any): Promise<Message> {
    const response = await this.client.post<Message>(
      `/chat/${chatbotId}/messages`,
      message
    );
    return response.data;
  }

  async checkChatbotReady(chatbotId: string): Promise<any> {
    const response = await this.client.get(`/chat/${chatbotId}/ready`);
    return response.data;
  }

  // ==================== Conversations ====================

  async getConversations(
    chatbotId?: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Conversation>> {
    const queryParams: Record<string, any> = { ...params };
    if (chatbotId) queryParams.chatbot_id = chatbotId;
    const response = await this.client.get<PaginatedResponse<Conversation>>(
      '/conversations',
      { params: queryParams }
    );
    return response.data;
  }

  async getConversation(id: string): Promise<Conversation> {
    const response = await this.client.get<Conversation>(`/conversations/${id}`);
    return response.data;
  }

  async createConversation(chatbotId: string, data?: ConversationCreate): Promise<Conversation> {
    const response = await this.client.post<Conversation>(
      `/chatbots/${chatbotId}/conversations`,
      data || {}
    );
    return response.data;
  }

  async renameConversation(id: string, update: ConversationUpdate): Promise<Conversation> {
    const response = await this.client.patch<Conversation>(
      `/conversations/${id}`,
      update
    );
    return response.data;
  }

  async deleteConversation(id: string): Promise<void> {
    await this.client.delete(`/conversations/${id}`);
  }

  async getConversationMessages(
    conversationId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Message>> {
    const response = await this.client.get<PaginatedResponse<Message>>(
      `/conversations/${conversationId}/messages`,
      { params }
    );
    return response.data;
  }

  async *conversationChatStream(
    conversationId: string,
    request: ConversationChatRequest
  ): AsyncGenerator<string | { sources: SourceDocument[] } | { conv: { id: string; title: string } } | { clarification: string }, void, unknown> {
    const streamBaseUrl = '/api';
    const response = await fetch(
      `${streamBaseUrl}/conversations/${conversationId}/chat/stream`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') {
              return;
            } else if (data.startsWith('[ERROR]')) {
              throw new Error(data.slice(7));
            } else if (data.startsWith('[SOURCES]')) {
              try {
                const sources: SourceDocument[] = JSON.parse(data.slice(9));
                yield { sources };
              } catch (e) {
                console.warn('Failed to parse sources:', e);
              }
            } else if (data.startsWith('[CONV]')) {
              try {
                const conv = JSON.parse(data.slice(6));
                yield { conv };
              } catch (e) {
                console.warn('Failed to parse conversation metadata:', e);
              }
            } else if (data.startsWith('[CLARIFICATION]')) {
              try {
                const clarificationData = JSON.parse(data.slice(15));
                yield { clarification: clarificationData.message || '' };
              } catch (e) {
                console.warn('Failed to parse clarification:', e);
              }
            } else {
              try {
                yield JSON.parse(data);
              } catch {
                yield data;
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  // ==================== Authentication (Future) ====================

  async createSession(userAgent?: string, ipAddress?: string): Promise<UserSession> {
    const response = await this.client.post<UserSession>('/auth/session', {
      user_agent: userAgent,
      ip_address: ipAddress,
    });
    return response.data;
  }

  async getSession(sessionToken: string): Promise<UserSession> {
    const response = await this.client.get<UserSession>(`/auth/session/${sessionToken}`);
    return response.data;
  }

  async deactivateSession(sessionToken: string): Promise<void> {
    await this.client.delete(`/auth/session/${sessionToken}`);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

export default apiClient;